// © Microsoft Corporation. All rights reserved.

import { CSSRules, ComponentStyleSheet, ComponentStyles } from "@microsoft/fast-jss-manager";
import {
    DesignSystem,
    DesignSystemDefaults,
    applyAcrylicMaterial,
    applyTypeRampConfig,
    fontWeight,
    highContrastSelector,
    neutralForegroundRest
} from "@microsoft/fast-components-styles-msft";
import { applyLocalizedProperty, toPx } from "@microsoft/fast-jss-utilities";
import { get, memoize } from "lodash-es";

import { DialogClassNameContract } from "@microsoft/fast-components-react-base";
import { ShareDialogClassNameContract } from "./ShareDialog.classnames";
import { ZIndex } from "@msnews/core";
import { canUseDOM } from "@msnews/isomorphic-util";

/** Default values for border radius */
const borderRadiusCopyLink = 2;
const borderRadiusDialog = 4;

const dialogContainerWidth = 322;

/**
 * Default color values for the share dialog
 */
const ShareDialogColors = {
    dividerLine: "rgba(0,0,0, 0.05)",
    inputText: "rgba(0, 1, 0, 0.6)",
    inputBorder: "rgba(0, 0, 0, 0.2)",
    copyLinkButtonBackground: "#0078D4",
    copyLinkButtonBackgroundActive: "#1B64B0",
    copyLinkButtonText: "#FFF",
    dialogOverlayBackground: "rgba(0, 0, 0, 0.4)"
};

/**
 * The JSS stylesheet for the base dialog
 * @type {ComponentStyles<DialogClassNameContract, DesignSystem>}
 * @param {DesignSystem} config - The design system configuration interface
 */
export const baseDialogStyles: ComponentStyles<DialogClassNameContract, DesignSystem> = (config: DesignSystem) => {
    const backgroundColor: string = get(config, "backgroundColor", DesignSystemDefaults.backgroundColor);

    return {
        dialog: {
            display: "none",
            '&[aria-hidden="false"]': {
                display: "block"
            }
        },
        dialog_modalOverlay: {
            position: "fixed",
            background: ShareDialogColors.dialogOverlayBackground,
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            "z-index": `${ZIndex.Above}`
        },
        dialog_contentRegion: {
            position: "fixed",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            "border-radius": toPx(borderRadiusDialog),
            "box-shadow": "none",
            "z-index": `${ZIndex.Above + 1}`,
            ...applyAcrylicMaterial(backgroundColor, 0.7, 0.7, true)
        }
    };
};

 /**
  * Gets the override style for base dialog based on ZIndex
  * @private
  * @method
  * @return {<ComponentStyleSheet<DialogClassNameContract, DesignSystem>>} the result style to be passed to base dialog
  */
export const setStyleByZIndex = memoize((overrides: ComponentStyleSheet<DialogClassNameContract, DesignSystem> = {}, zIndex: number = null) => {
    return zIndex == null ? overrides : {
        ...overrides,
        dialog_modalOverlay: {
            ...overrides.dialog_modalOverlay,
            "z-index": zIndex.toString()
        },
        dialog_contentRegion: {
            ...overrides.dialog_contentRegion,
            "z-index": (zIndex + 1).toString()
        }
    };
});

/**
 * The JSS stylesheet for the share dialog
 * @type {ComponentStyles<ShareDialogClassNameContract, DesignSystem>}
 * @param {DesignSystem} config - The design system configuration interface
 */
export const ShareDialogStyles: ComponentStyles<ShareDialogClassNameContract, DesignSystem> = (config: DesignSystem) => {
    const iconWidth = 40;
    const headerLineHeight = 24;
    const backdropFilterStyles: CSSRules<DesignSystem> = {
        background: "none"
    };
    const fallbackStyles: CSSRules<DesignSystem> = {
        background: get(config, "backgroundColor", DesignSystemDefaults.backgroundColor)
    };
    const backdropFilterSupport: boolean = canUseDOM() && ("backdrop-filter" in document.documentElement.style || "-webkit-backdrop-filter" in document.documentElement.style);
    const backgroundStyle: CSSRules<DesignSystem> = backdropFilterSupport ? backdropFilterStyles : fallbackStyles;
    return {
        closeButton: {
            background: "transparent !important",
            border: "none",
            height: `${headerLineHeight}px !important`,
            padding: "0",
            position: "absolute",
            "min-width": toPx(headerLineHeight),
            [applyLocalizedProperty("right", "left", config.direction)]: "4px",
            "&:hover, &:focus, &:active": {
                cursor: "pointer",
                outline: "auto"
            },
            [highContrastSelector]: {
                background: "ButtonFace !important",
                "& svg": {
                    fill: "ButtonText"
                },
                "&:hover, &:focus, &:active": {
                    background: "Highlight !important",
                    "& svg": {
                        fill: "HighlightText"
                    }
                }
            }
        },
        closeIcon: {
            fill: neutralForegroundRest
        },
        copyLinkButton: {},
        copyLinkInput: {},
        dialogContainer: {
            ...backgroundStyle,
            "border-radius": toPx(borderRadiusDialog),
            padding: "0 12px",
            width: toPx(dialogContainerWidth),
            "box-sizing": "content-box",
            "text-align": "center",
            "& $inputContainer": {
                "& $copyLinkInput": {
                    [applyLocalizedProperty("border-top-left-radius", "border-top-right-radius", config.direction)]: toPx(borderRadiusCopyLink),
                    [applyLocalizedProperty("border-bottom-left-radius", "border-bottom-right-radius", config.direction)]: toPx(borderRadiusCopyLink)
                }
            },
            [highContrastSelector]: {
                border: "1px solid Highlight"
            }
        },
        header: {
            "border-bottom": `${config.outlineWidth}px solid ${ShareDialogColors.dividerLine}`,
            display: "flex",
            "line-height": toPx(headerLineHeight),
            padding: "12px 0",
            "text-align": "center"
        },
        headerText: {
            ...applyTypeRampConfig("t7"),
            "font-weight": `${fontWeight.semibold}`,
            margin: "0",
            width: "100%"
        },
        iconListContainer: {
            display: "inline-block",
            "list-style": "none",
            "text-align": "center",
            "margin-top": "36px",
            padding: "0",
            width: "inherit"
        },
        iconListItem: {
            float: applyLocalizedProperty("left", "right", config.direction),
            width: "25%",
            "&:nth-child(n+5)": {
                "margin-top": "24px"
            },
            "&:nth-child(4n+1)": {
                clear: "both"
            }
        },
        inputContainer: {
            "box-sizing": "content-box",
            "padding-top": "24px",
            "padding-bottom": "32px",
            "text-align": "center",
            "& $copyLinkInput": {
                border: `2px solid ${ShareDialogColors.inputBorder}`,
                "box-sizing": "content-box",
                color: ShareDialogColors.inputText,
                ...applyTypeRampConfig("t8"),
                padding: "1px 0 1px 5px",
                height: "28px",
                width: "191px"
            },
            "& $copyLinkButton": {
                background: ShareDialogColors.copyLinkButtonBackground,
                [applyLocalizedProperty("border-top-right-radius", "border-top-left-radius", config.direction)]: toPx(borderRadiusCopyLink),
                [applyLocalizedProperty("border-bottom-right-radius", "border-bottom-left-radius", config.direction)]: toPx(borderRadiusCopyLink),
                border: "none",
                color: ShareDialogColors.copyLinkButtonText,
                ...applyTypeRampConfig("t8"),
                height: "32px",
                "vertical-align": "middle",
                width: "89px",
                "&:hover": {
                    cursor: "pointer"
                },
                "&:active": {
                    background: ShareDialogColors.copyLinkButtonBackgroundActive
                },
                [highContrastSelector]: {
                    background: "ButtonFace",
                    color: "ButtonText",
                    "&:hover, &:focus, &:active": {
                        background: "Highlight",
                        color: "HighlightText"
                    }
                }
            }
        }
    };
};